<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import { ICON_BUTTON_CLASS, SURVEY_ROUTE_NAMES } from '../constants';
import { responseRate } from '../helper/surveyFormHelper';
import { useSurveyLabels } from '../composables/useSurveyLabels';

/**
 * One form in the list: identity, delivery stats and every row action.
 * Edit/responses are real links (mid-click friendly), the destructive and
 * state-changing actions are emitted so the page owns confirmation and alerts.
 */
const props = defineProps({
  form: { type: Object, required: true },
  busyAction: { type: String, default: '' },
});

const emit = defineEmits(['duplicate', 'toggle', 'delete', 'friendly-link']);

const route = useRoute();
const { t, kindLabel, kindIcon } = useSurveyLabels();

const accountId = computed(() => route.params.accountId);

const editRoute = computed(() => ({
  name: SURVEY_ROUTE_NAMES.edit,
  params: { accountId: accountId.value, formId: props.form.id },
}));

const responsesRoute = computed(() => ({
  name: SURVEY_ROUTE_NAMES.responses,
  params: { accountId: accountId.value, formId: props.form.id },
}));

const rate = computed(() => responseRate(props.form));
const formattedRate = computed(() => `${rate.value}%`);
const questionCount = computed(() => props.form.questions?.length || 0);
const isBusy = computed(() => Boolean(props.busyAction));
const hasResponses = computed(() => Number(props.form.responses_count) > 0);
const deletionRestrictionId = computed(
  () => `survey-form-${props.form.id}-deletion-restriction`
);
const PUBLIC_LINK_FORM_KINDS = [
  'visit',
  'proposal',
  'listing',
  'custom',
  'visit_feedback',
  'support',
  'post_sale',
  'kyc',
];
const supportsFriendlyLink = computed(
  () => props.form.active && PUBLIC_LINK_FORM_KINDS.includes(props.form.kind)
);

const toggleLabel = computed(() =>
  props.form.active
    ? t('SURVEYS.LIST.ACTIONS.DEACTIVATE')
    : t('SURVEYS.LIST.ACTIONS.ACTIVATE')
);

const statusTone = computed(() => (props.form.active ? 'positive' : 'neutral'));
const statusLabel = computed(() =>
  props.form.active
    ? t('SURVEYS.LIST.STATUS.ACTIVE')
    : t('SURVEYS.LIST.STATUS.INACTIVE')
);

const deleteLabel = computed(() =>
  hasResponses.value
    ? t('SURVEYS.LIST.DELETE.BLOCKED_LABEL')
    : t('SURVEYS.LIST.ACTIONS.DELETE')
);

const requestDelete = () => {
  if (!hasResponses.value) emit('delete');
};
</script>

<template>
  <LaisCard density="tight">
    <template #header>
      <div class="flex min-w-0 flex-1 items-start gap-3">
        <span
          class="mt-0.5 grid size-9 shrink-0 place-items-center rounded-lais bg-lais-primary-50 text-lais-primary"
        >
          <span
            class="size-4"
            :class="kindIcon(form.kind)"
            aria-hidden="true"
          />
        </span>
        <div class="min-w-0 flex-1">
          <h3
            class="truncate font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
          >
            {{ form.name }}
          </h3>
          <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
            <LaisStatusBadge
              :label="kindLabel(form.kind)"
              tone="info"
              size="sm"
            />
            <LaisStatusBadge
              :label="statusLabel"
              :tone="statusTone"
              size="sm"
              dot
            />
            <LaisStatusBadge
              v-if="form.default_for_kind"
              :label="t('SURVEYS.LIST.STATUS.DEFAULT')"
              tone="warning"
              size="sm"
              icon="i-lucide-pin"
            />
          </div>
        </div>
      </div>
    </template>

    <p
      v-if="form.description"
      class="line-clamp-2 text-lais-body-sm text-lais-text-body"
    >
      {{ form.description }}
    </p>
    <p v-else class="text-lais-body-sm italic text-lais-text-muted">
      {{ t('SURVEYS.LIST.NO_DESCRIPTION') }}
    </p>

    <dl class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
      <div class="min-w-0">
        <dt class="text-lais-label-xs uppercase text-lais-text-muted">
          {{ t('SURVEYS.LIST.STATS.QUESTIONS') }}
        </dt>
        <dd
          class="font-redHatDisplay text-lais-heading-sm font-semibold tabular-nums text-lais-text"
        >
          {{ questionCount }}
        </dd>
      </div>
      <div class="min-w-0">
        <dt class="text-lais-label-xs uppercase text-lais-text-muted">
          {{ t('SURVEYS.LIST.STATS.RESPONSES') }}
        </dt>
        <dd
          class="font-redHatDisplay text-lais-heading-sm font-semibold tabular-nums text-lais-text"
        >
          {{ form.submitted_count }}
          <span class="text-lais-body-sm font-normal text-lais-text-muted">
            {{
              t('SURVEYS.LIST.STATS.OF_SENT', { count: form.responses_count })
            }}
          </span>
        </dd>
      </div>
      <div class="col-span-2 min-w-0 sm:col-span-1">
        <dt class="text-lais-label-xs uppercase text-lais-text-muted">
          {{ t('SURVEYS.LIST.STATS.RATE') }}
        </dt>
        <dd class="flex items-center gap-2">
          <span
            class="font-redHatDisplay text-lais-heading-sm font-semibold tabular-nums text-lais-text"
          >
            {{ formattedRate }}
          </span>
          <span
            class="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-lais-bg-soft"
          >
            <span
              class="block h-full rounded-full bg-lais-primary"
              :style="{ width: `${rate}%` }"
            />
          </span>
        </dd>
      </div>
    </dl>

    <template #footer>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex flex-wrap items-center gap-2">
          <router-link
            :to="editRoute"
            class="inline-flex h-9 items-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary focus-visible:outline-none focus-visible:shadow-lais-focus"
          >
            <span class="i-lucide-pencil size-3.5" aria-hidden="true" />
            {{ t('SURVEYS.LIST.ACTIONS.EDIT') }}
          </router-link>
          <router-link
            :to="responsesRoute"
            class="inline-flex h-9 items-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary focus-visible:outline-none focus-visible:shadow-lais-focus"
          >
            <span class="i-lucide-bar-chart-3 size-3.5" aria-hidden="true" />
            {{ t('SURVEYS.LIST.ACTIONS.RESPONSES') }}
          </router-link>
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary focus-visible:outline-none focus-visible:shadow-lais-focus"
            :aria-label="t('SURVEYS.LIST.ACTIONS.CREATE_VERSION')"
            :disabled="isBusy"
            @click="emit('duplicate')"
          >
            <span
              v-if="busyAction === 'duplicate'"
              class="i-lucide-loader-circle size-3.5 animate-spin"
              aria-hidden="true"
            />
            <span v-else class="i-lucide-copy size-3.5" aria-hidden="true" />
            {{ t('SURVEYS.LIST.ACTIONS.CREATE_VERSION') }}
          </button>
          <button
            v-if="supportsFriendlyLink"
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary focus-visible:outline-none focus-visible:shadow-lais-focus"
            :disabled="isBusy"
            @click="emit('friendly-link')"
          >
            <span class="i-lucide-link-2 size-3.5" aria-hidden="true" />
            {{ t('SURVEYS.LIST.ACTIONS.FRIENDLY_LINK') }}
          </button>
        </div>

        <div class="flex flex-col items-end gap-1.5">
          <div class="flex items-center gap-2">
            <button
              v-tooltip.top="toggleLabel"
              type="button"
              :class="ICON_BUTTON_CLASS"
              :aria-label="toggleLabel"
              :disabled="isBusy"
              @click="emit('toggle')"
            >
              <span
                v-if="busyAction === 'toggle'"
                class="i-lucide-loader-circle size-4 animate-spin"
                aria-hidden="true"
              />
              <span
                v-else
                class="size-4"
                :class="form.active ? 'i-lucide-pause' : 'i-lucide-play'"
                aria-hidden="true"
              />
            </button>
            <button
              v-tooltip.top="deleteLabel"
              type="button"
              :class="ICON_BUTTON_CLASS"
              class="hover:!border-lais-danger hover:!text-lais-danger"
              :aria-describedby="
                hasResponses ? deletionRestrictionId : undefined
              "
              :aria-label="deleteLabel"
              :disabled="isBusy || hasResponses"
              @click="requestDelete"
            >
              <span
                v-if="busyAction === 'delete'"
                class="i-lucide-loader-circle size-4 animate-spin"
                aria-hidden="true"
              />
              <span v-else class="i-lucide-trash-2 size-4" aria-hidden="true" />
            </button>
          </div>
          <p
            v-if="hasResponses"
            :id="deletionRestrictionId"
            class="max-w-64 text-right text-lais-label-xs leading-relaxed text-lais-text-helper"
            role="note"
          >
            {{ t('SURVEYS.LIST.DELETE.BLOCKED') }}
          </p>
        </div>
      </div>
    </template>
  </LaisCard>
</template>
